// Face resolver (face templates spec sections 2 and 4). Pure and renderer-independent: flattens
// inheritance, matches faces to objects, and resolves every layer's bindings, conditions and
// detail level into plain values that the SVG renderer (or any later renderer) draws.

import { parseBinding, resolveBinding, type Binding, type BindingContext, type Resolved } from '../presentation/bindings.ts';
import { COLOR_TOKENS } from '../presentation/types.ts';
import { builtinFace, isBuiltin } from './builtin.ts';
import type { Box, FaceDef, FaceMatch, GradientStop, Layer, Lod, Paint, TextEffect } from './types.ts';
import type { Shape } from '../presentation/types.ts';
import type { ArtResult } from './art.ts';
import { parseRichText, resolveRichValues, type RichNode, type RichStyle } from './richText.ts';

export interface FlatFace {
  id: string;
  size: { w: number; h: number; radius: number };
  match: FaceMatch;
  layers: Layer[];
  back: Layer[] | null;
  lod: FaceDef['lod'];
  /** Drawn by the presentation-1 card renderer (a v1 template). */
  legacy?: boolean;
  shape?: Shape;
}

const MAX_DEPTH = 4;
const parsed = new Map<string, Binding | string>();
export function binding(text: string): Binding | string {
  let b = parsed.get(text);
  if (b === undefined) { b = parseBinding(text); parsed.set(text, b); }
  return b;
}

/** A layer reference: its `id`, or `type#n` (1-based among layers of that type). */
export function findLayer(layers: Layer[], ref: string): number {
  const hash = ref.indexOf('#');
  if (hash > 0) {
    const type = ref.slice(0, hash), n = Number(ref.slice(hash + 1));
    let seen = 0;
    return layers.findIndex(l => l.type === type && ++seen === n);
  }
  return layers.findIndex(l => l.id === ref);
}

/** Resolves `extends`, `add`, `replace` and `remove`. Throws on a missing parent, cycle or reference. */
export function flatten(id: string, faces: Map<string, FaceDef>, depth = 0, seen: string[] = []): FlatFace {
  if (seen.includes(id)) throw new Error(`Face "${id}" extends itself through ${seen.join(' → ')}.`);
  if (depth > MAX_DEPTH) throw new Error(`Face "${id}" extends more than ${MAX_DEPTH} levels.`);
  const def = isBuiltin(id) ? builtinFace(id.slice(8) as Shape) : faces.get(id);
  if (!def) throw new Error(`Face "${id}" does not exist.`);
  let layers: Layer[];
  let size = def.size;
  let match = def.match;
  let back = def.back ?? null;
  let lod = def.lod;
  if (def.extends) {
    const parent = flatten(def.extends, faces, depth + 1, [...seen, id]);
    layers = [...parent.layers];
    size = size ?? parent.size;
    match = match ?? parent.match;
    back = back ?? parent.back;
    lod = lod ?? parent.lod;
    for (const ref of def.remove ?? []) {
      const i = findLayer(layers, ref);
      if (i < 0) throw new Error(`Face "${id}" removes unknown layer "${ref}".`);
      layers.splice(i, 1);
    }
    for (const [ref, layer] of Object.entries(def.replace ?? {})) {
      const i = findLayer(layers, ref);
      if (i < 0) throw new Error(`Face "${id}" replaces unknown layer "${ref}".`);
      layers[i] = layer;
    }
    for (const layer of def.add ?? []) {
      const { after, before, ...rest } = layer;
      const ref = after ?? before;
      if (ref === undefined) { layers.push(rest as Layer); continue; }
      const i = findLayer(layers, ref);
      if (i < 0) throw new Error(`Face "${id}" adds a layer relative to unknown layer "${ref}".`);
      layers.splice(after !== undefined ? i + 1 : i, 0, rest as Layer);
    }
    layers.push(...(def.layers ?? []));
  } else {
    layers = [...(def.layers ?? [])];
  }
  if (!size) throw new Error(`Face "${id}" has no size.`);
  return { id, size: { radius: 0, ...size }, match: match ?? {}, layers, back, lod };
}

const SPECIFICITY = (m: FaceMatch) => (m.catalog ? 3 : m.tags ? 2 : m.kind ? 1 : 0);

export interface MatchTarget { kind: string; catalogKey: string | null; tags: string[]; faceDown: boolean }

/** The most specific face whose match the object satisfies; ties keep file order. */
export function matchFace<F extends { match: FaceMatch }>(faces: F[], o: MatchTarget, zoneKind: string | null): F | null {
  let best: F | null = null, score = -1;
  for (const f of faces) {
    const m = f.match;
    if (m.kind && m.kind !== o.kind) continue;
    if (m.catalog && (!o.catalogKey || !m.catalog.includes(o.catalogKey))) continue;
    if (m.tags && !m.tags.every(t => o.tags.includes(t))) continue;
    if (m.zoneKind && m.zoneKind !== zoneKind) continue;
    if (m.face && m.face !== (o.faceDown ? 'down' : 'up')) continue;
    const s = SPECIFICITY(m);
    if (s > score) { best = f; score = s; }
  }
  return best;
}

/** Detail level from the drawn width in CSS pixels (spec section 2.4). */
export function lodForWidth(px: number): Lod {
  return px < 40 ? 'chip' : px < 90 ? 'thumb' : px <= 240 ? 'full' : 'zoom';
}

// ---- Resolution ------------------------------------------------------------------------

export type ResolvedPaint =
  | { kind: 'color'; value: string }
  | { kind: 'linear'; stops: [string, number][]; angle: number }
  | { kind: 'radial'; stops: [string, number][]; at: [number, number] };

export type ResolvedIcon = { kind: 'glyph'; name: string } | Extract<Resolved, { kind: 'image' }>;
export type ResolvedRichNode = Exclude<RichNode, { type: 'icon' | 'value' }> | { type: 'icon'; key: string; icon: ResolvedIcon | null; style: RichStyle };

interface Common { id?: string; box: Box; opacity: number; blend: string; rotate: number; mask: string }

export type ResolvedLayer = Common & (
  | { type: 'fill'; paint: ResolvedPaint }
  | { type: 'image'; url: string | null; pattern: string | null; fit: string; focus: [number, number];
      /** Renderer-owned public piece tint, applied multiplicatively with source alpha retained. */
      tint?: string;
      /** An atlas frame: the drawn region of a sheet `url` of size `sheet`. */
      frame?: { x: number; y: number; w: number; h: number }; sheet?: { w: number; h: number } }
  | { type: 'text'; text: string; rich?: ResolvedRichNode[]; padding?: [number, number, number, number]; lineHeight?: number; role: string; ink: ResolvedPaint | null; align: 'start' | 'center' | 'end'; valign: 'top' | 'middle' | 'bottom'; fit: 'shrink' | 'clamp' | 'wrap'; style: string; effects: TextEffect[]; scale?: number }
  | { type: 'icon'; icon: ResolvedIcon; paint: ResolvedPaint | null }
  | { type: 'icon-row'; items: { key: string; label: string; value: number; icon: ResolvedIcon | null }[]; iconSize: number; align: string; gap: number; style: string; numberKeys: string[]; paint?: ResolvedPaint | null }
  | { type: 'badge'; text: string | null; icon: ResolvedIcon | null; shape: string; paint: ResolvedPaint | null; ink: ResolvedPaint | null; effects: TextEffect[] }
  | { type: 'frame'; style: string; paint: ResolvedPaint | null; width: number }
  | { type: 'shape'; shape: string; paint: ResolvedPaint | null; stroke: { paint: ResolvedPaint; width: number } | null }
  | { type: 'pattern'; pattern: string; paint: ResolvedPaint | null; scale: number }
  | { type: 'slot'; slot: string; value: Resolved | null }
);

/** Art lookups that fell back or named a missing frame (for the contact sheet). */
export interface ArtNote { slot: string; fellBack: boolean; missing: string | null }

export interface ResolvedFace { id: string; size: { w: number; h: number; radius: number }; lod: Lod; layers: ResolvedLayer[]; legacy: boolean; shape?: Shape; art?: ArtNote[] }

export interface FaceContext extends BindingContext {
  /** Theme color token to a CSS color (usually a `var(--…)`). */
  token: (name: string) => string | null;
  /** Art slot lookup for this object (art.json); absent without atlases. */
  art?: (slot: string) => ArtResult;
}

const HEX_PAINT = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const TOKENS = new Set<string>(COLOR_TOKENS);

function bound(text: string | undefined, ctx: FaceContext): Resolved | null {
  if (text === undefined || text === '') return null;
  const b = binding(text);
  return typeof b === 'string' ? null : resolveBinding(b, ctx);
}

function colorOf(text: string, ctx: FaceContext): string | null {
  if (HEX_PAINT.test(text)) return text;
  if (TOKENS.has(text)) return ctx.token(text);
  const r = bound(text, ctx);
  return r?.kind === 'color' ? r.value : null;
}

function stops(list: GradientStop[], ctx: FaceContext): [string, number][] | null {
  const out: [string, number][] = [];
  list.forEach((s, i) => {
    const [color, offset] = Array.isArray(s) ? s : [s, list.length === 1 ? 0 : i / (list.length - 1)];
    const c = colorOf(color, ctx);
    if (c) out.push([c, offset]);
  });
  return out.length ? out : null;
}

export function resolvePaint(paint: Paint | undefined, ctx: FaceContext): ResolvedPaint | null {
  if (paint === undefined) return null;
  if (typeof paint === 'string') { const c = colorOf(paint, ctx); return c ? { kind: 'color', value: c } : null; }
  if ('linear' in paint) { const s = stops(paint.linear, ctx); return s ? { kind: 'linear', stops: s, angle: paint.angle ?? 180 } : null; }
  const s = stops(paint.radial, ctx);
  return s ? { kind: 'radial', stops: s, at: paint.at ?? [50, 50] } : null;
}

function iconOf(text: string | undefined, ctx: FaceContext): ResolvedIcon | null {
  if (!text) return null;
  if (text.startsWith('gi:')) return { kind: 'glyph', name: text };
  const r = text.includes('|') || text.startsWith('fields') || text.startsWith('catalog') || text.startsWith('object') ? bound(text, ctx) : ctx.icon(text);
  if (r?.kind === 'glyph') return { kind: 'glyph', name: r.name };
  if (r?.kind === 'image') return r;
  return null;
}

function textOf(text: string | undefined, ctx: FaceContext): string | null {
  const r = bound(text, ctx);
  return r?.kind === 'text' ? r.text : null;
}

/** `when` draws only for a non-empty, non-zero value; `unless` only when it does not resolve. */
function shows(layer: Layer, ctx: FaceContext): boolean {
  const value = (text: string) => { const r = bound(text, ctx); return r !== null && !(r.kind === 'text' && (r.text === '' || r.text === '0' || r.text === 'false')); };
  if (layer.when !== undefined && !value(layer.when)) return false;
  if (layer.unless !== undefined && value(layer.unless)) return false;
  return true;
}

export function resolveFace(face: FlatFace, ctx: FaceContext, lod: Lod, side: 'front' | 'back' = 'front'): ResolvedFace {
  const override = side === 'front' ? face.lod?.[lod] : undefined;
  const size = override?.size ? { radius: face.size.radius, ...override.size } : face.size;
  let layers = side === 'back' ? face.back ?? [] : face.layers;
  if (override?.layers) {
    const keep = new Set(override.layers.map(ref => layers[findLayer(layers, ref)]).filter(Boolean));
    layers = layers.filter(l => keep.has(l));
  }
  const out: ResolvedLayer[] = [];
  const notes: ArtNote[] = [];
  for (const layer of layers) {
    if (layer.lod && !layer.lod.includes(lod)) continue;
    if ((lod === 'chip') && layer.type === 'text' && (layer.role === 'rules' || layer.role === 'flavor')) continue;
    if (!shows(layer, ctx)) continue;
    const common: Common = {
      ...(layer.id ? { id: layer.id } : {}), box: layer.box ?? [0, 0, 100, 100], opacity: layer.opacity ?? 1,
      blend: layer.blend ?? 'normal', rotate: layer.rotate ?? 0, mask: layer.mask ?? 'none',
    };
    const r = resolveLayer(layer, common, ctx, notes);
    if (r) out.push(r);
  }
  return { id: face.id, size, lod, layers: out, legacy: !!face.legacy, ...(face.shape ? { shape: face.shape } : {}), ...(notes.length ? { art: notes } : {}) };
}

function resolveLayer(layer: Layer, common: Common, ctx: FaceContext, notes: ArtNote[]): ResolvedLayer | null {
  switch (layer.type) {
    case 'fill': { const paint = resolvePaint(layer.paint, ctx); return paint ? { ...common, type: 'fill', paint } : null; }
    case 'image': {
      let url: string | null = null;
      if (layer.asset) { const r = bound(layer.asset.startsWith('asset:') ? layer.asset : `asset:${layer.asset}`, ctx); url = r?.kind === 'image' ? r.url : null; }
      const fit = layer.fit ?? 'cover';
      if (url) return { ...common, type: 'image', url, pattern: null, fit, focus: layer.focus ?? [50, 50] };
      if (!layer.art || !ctx.art) return null;
      const art = ctx.art(layer.art);
      if (art.fellBack || art.missing) notes.push({ slot: layer.art, fellBack: art.fellBack, missing: art.missing });
      const v = art.value;
      if (!v) return null;
      if (v.kind === 'pattern') return { ...common, type: 'image', url: null, pattern: v.pattern, fit, focus: [50, 50] };
      return { ...common, type: 'image', url: v.url, pattern: null, fit, focus: v.focus ?? layer.focus ?? v.pivot, frame: v.frame, sheet: v.sheet };
    }
    case 'text': {
      const text = textOf(layer.text, ctx);
      if (text === null) return null;
      let rich: ResolvedRichNode[] | undefined;
      try {
        rich = layer.rich === undefined ? undefined : resolveRichValues(parseRichText(layer.rich), value => textOf(value, ctx))
          .map(node => node.type === 'icon' ? { type: 'icon' as const, key: node.key, icon: iconOf(node.key, ctx), style: node.style } : node) as ResolvedRichNode[];
      } catch { /* A resolved content budget failure keeps the plain-text face. */ }
      return {
        ...common, type: 'text', text, role: layer.role ?? 'caption', ink: resolvePaint(layer.ink, ctx), align: layer.align ?? 'start',
        valign: layer.valign ?? 'middle', fit: layer.fit ?? 'shrink', style: layer.style ?? 'normal', effects: (layer.effects ?? []).slice(0, 3),
        scale: layer.scale ?? 1,
        ...(rich ? { rich } : {}), ...(layer.padding ? { padding: layer.padding } : {}), ...(layer.lineHeight ? { lineHeight: layer.lineHeight } : {}),
      };
    }
    case 'icon': { const icon = iconOf(layer.icon, ctx); return icon ? { ...common, type: 'icon', icon, paint: resolvePaint(layer.paint, ctx) } : null; }
    case 'icon-row': {
      const r = bound(layer.source, ctx);
      if (r?.kind !== 'costs' || r.items.length === 0) return null;
      const items = r.items.map(i => ({ key: i.key, label: i.label, value: i.value, icon: i.icon?.kind === 'glyph' ? { kind: 'glyph' as const, name: i.icon.name } : i.icon?.kind === 'image' ? i.icon : null }));
      return { ...common, type: 'icon-row', items, iconSize: layer.iconSize ?? 7, align: layer.align ?? 'start', gap: layer.gap ?? 1.5, style: layer.style ?? 'icon-count', numberKeys: layer.numberKeys ?? [], paint: resolvePaint(layer.paint, ctx) };
    }
    case 'badge': {
      const text = textOf(layer.text, ctx), icon = iconOf(layer.icon, ctx);
      if (text === null && icon === null) return null;
      return { ...common, type: 'badge', text, icon, shape: layer.shape ?? 'circle', paint: resolvePaint(layer.paint, ctx), ink: resolvePaint(layer.ink, ctx), effects: (layer.effects ?? []).slice(0, 3) };
    }
    case 'frame': return { ...common, type: 'frame', style: layer.style ?? 'classic', paint: resolvePaint(layer.paint, ctx), width: layer.width ?? 1 };
    case 'shape': {
      const stroke = layer.stroke ? resolvePaint(layer.stroke.paint, ctx) : null;
      return { ...common, type: 'shape', shape: layer.shape, paint: resolvePaint(layer.paint, ctx), stroke: stroke ? { paint: stroke, width: layer.stroke!.width } : null };
    }
    case 'pattern': return { ...common, type: 'pattern', pattern: layer.pattern, paint: resolvePaint(layer.paint, ctx), scale: layer.scale ?? 1 };
    case 'slot': return { ...common, type: 'slot', slot: layer.slot, value: bound(layer.binding, ctx) };
  }
}

/** The v1 slot values of a resolved built-in face, for the presentation-1 card renderer. */
export function slotValues(face: ResolvedFace): Record<string, Resolved | null> {
  const slots: Record<string, Resolved | null> = {};
  for (const l of face.layers) if (l.type === 'slot') slots[l.slot] = l.value;
  return slots;
}
